/* eslint-disable import/exports-last */

const GrowthDir = {
  Grow: 1,
  Shrink: -1,
} as const
type GrowthDir = (typeof GrowthDir)[keyof typeof GrowthDir]

export type ColumnWidth = {
  width: number
  resizable: boolean
  minimize?: boolean
  lastResizable?: true
  maxWidth?: number
  minWidth?: number
  field: string
}
export class GrowthMap {
  private map: Record<GrowthDir, number> = {
    [GrowthDir.Shrink]: 0,
    [GrowthDir.Grow]: 0,
  }

  constructor(shrink: number, grow: number) {
    this.map[GrowthDir.Shrink] = shrink
    this.map[GrowthDir.Grow] = grow
  }

  get(dir: GrowthDir) {
    return this.map[dir]
  }

  set(dir: GrowthDir, value: number) {
    this.map[dir] = value
  }

  add(dir: GrowthDir, value: number) {
    this.map[dir] += value
  }
}

export function addGrowthData(
  col: ColumnWidth,
  width: number,
  available: Record<string, GrowthMap>,
  total: GrowthMap,
  columnCounter: GrowthMap
): void {
  if (!col.resizable) return
  const shrink = width - (col.minWidth ?? 0)
  const grow = (col.maxWidth ?? width) - width
  available[col.field] = new GrowthMap(shrink, grow)
  total.add(GrowthDir.Shrink, shrink)
  total.add(GrowthDir.Grow, grow)
  columnCounter.add(GrowthDir.Shrink, shrink ? 1 : 0)
  columnCounter.add(GrowthDir.Grow, grow ? 1 : 0)
}

// Comeback to the topic of multiple columns growing/shrinking at the same time
export function calcWidthChange(
  remainingSpace: number,
  totalAvailGrowth: number,
  growthPerColumn: number,
  growthConstraint: number,
  cellIsResizing = false
): number {
  return Math.min(growthConstraint, remainingSpace)
  // let change = growthPerColumn
  // const remainingGrowthPotential = totalAvailGrowth - growthPerColumn
  // if (remainingGrowthPotential < remainingSpace) {
  //   change += remainingSpace - remainingGrowthPotential
  // }
  // change = Math.min(change, remainingSpace, growthConstraint)
  // return change
}

export function getColumnWidth(col: ColumnWidth, widths: Record<string, number | undefined>): number {
  const widthSet: number | undefined = widths[col.field]
  let width = (widthSet || col.width || col.minWidth) ?? 0
  const minWidth = col.minWidth ?? 0
  const maxWidth = col.maxWidth ?? Number.POSITIVE_INFINITY
  width = Math.max(width, minWidth)
  width = Math.min(width, maxWidth)
  return width
}

function calculateColumnWidths<T extends ColumnWidth>(
  columnIds: string[],
  columns: Record<string, T>,
  widths: Record<string, number | undefined>,
  containerWidth: number = 0,
  expandable: boolean,
  cellIsResizing = false
): Array<ColumnWidth> {
  let availWidth = containerWidth
  const calculatedColumns: ColumnWidth[] = []
  const availColGrowth: Record<string, GrowthMap> = {}
  const totalAvailGrowth = new GrowthMap(0, 0)
  const availColCnt = new GrowthMap(0, 0)

  for (const key of columnIds) {
    const col = columns[key]!
    const width = getColumnWidth(col, widths)
    addGrowthData(col, width, availColGrowth, totalAvailGrowth, availColCnt)
    availWidth -= width
    calculatedColumns.push({ ...col, width })
  }
  if (expandable || containerWidth === 0 || availWidth === 0) return calculatedColumns

  const dir = availWidth > 0 ? GrowthDir.Grow : GrowthDir.Shrink
  const count = availColCnt.get(dir)
  if (!count) return calculatedColumns

  let remaining = Math.abs(availWidth)
  const growthPerCol = Math.ceil(remaining / count)

  for (let i = columnIds.length - 1; i >= 0; i--) {
    const col = calculatedColumns[i]!
    if (!col.resizable) continue
    const colGrowthConstraint = availColGrowth[col.field]?.get(dir)
    if (!colGrowthConstraint) continue
    const change = calcWidthChange(
      remaining,
      totalAvailGrowth.get(dir),
      growthPerCol,
      colGrowthConstraint,
      cellIsResizing
    )
    totalAvailGrowth.set(dir, totalAvailGrowth.get(dir) - change)
    remaining -= change
    col.width += change * dir
    if (remaining === 0) break
  }
  return calculatedColumns
}

export default calculateColumnWidths
